import React from "react";
import { Box } from "@UI/Components/Box/Box";
import { Skeleton } from "@UI/Components/Skeleton/Skeleton";

interface Props {
  coverHeight?: string | number;
}

/**
 * RecommendationCardSkeleton
 *
 * Loading placeholder that mirrors the exact structure of RecommendationCard:
 * - Outer container: borderRadius="3xl" + overflow="hidden" (clips image corners
 *   and the bottom of the inner card)
 * - Cover image block
 * - Inner card: borderRadius="3xl" + marginTop="-4" (the overlapping rounded peek)
 *   - Header slot: title line
 *   - Body slot (flexGrow=1): description lines
 *   - Footer slot (marginTop="auto"): star icon + rating text
 */
export const RecommendationCardSkeleton = ({
  coverHeight = "150px",
}: Props) => (
  <Box
    display="flex"
    flexDirection="column"
    alignSelf="stretch"
    flex="1"
    borderRadius="3xl"
    overflow="hidden"
    boxShadow="rt-lg"
  >
    {/* Cover — borderRadius=0 so the outer container does the corner clipping */}
    <Skeleton
      height={coverHeight}
      width="100%"
      flexShrink={0}
      borderRadius={0}
    />

    {/* Inner card — matches Card's rounded="3xl" + marginTop="-4" + padding="4" */}
    <Box
      backgroundColor="white"
      borderRadius="3xl"
      marginTop="-4"
      padding="4"
      flex="1"
      display="flex"
      flexDirection="column"
      gap="1"
      zIndex={1}
    >
      {/* Header: bold title (fontSize="md" → ~16px) */}
      <Skeleton height="16px" width="62%" borderRadius="md" marginBottom="1" />

      {/* Body: 3-line description (fontSize="xs" → ~12px), flexGrow=1 */}
      <Box flexGrow={1} display="flex" flexDirection="column" gap="1">
        <Skeleton height="12px" width="100%" borderRadius="md" />
        <Skeleton height="12px" width="90%" borderRadius="md" />
        <Skeleton height="12px" width="60%" borderRadius="md" />
      </Box>

      {/* Footer: ⭐ icon + "4.79/5 (123 avis)" pushed to bottom */}
      <Box
        display="flex"
        alignItems="center"
        gap="2"
        marginTop="auto"
        paddingTop="3"
      >
        <Skeleton height="14px" width="14px" borderRadius="sm" flexShrink={0} />
        <Skeleton height="12px" width="85px" borderRadius="md" />
      </Box>
    </Box>
  </Box>
);
